import { Box, ButtonBase, IconButton, Typography, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilCallback, useSetRecoilState } from "recoil";

import { layoutTokens as layout, semanticColors as colors, textStyles } from "@/designTokens";
import { useDevice } from "@/hooks/useDevice";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import theme from "@/theme";
import { getMonthlyRentByProperty, isPropertyCurrentInUse } from "@/utilities/calculateProperty";
import { getPropertyAddressString } from "@/utilities/getPropertyAddressString";
import { formatYieldRateForDisplay, getActualYieldRateByProperty, getYieldRateByProperty } from "@/utilities/yield";
import {
  PropertyEditPageState,
  PropertyHolder,
  PropertyManagementDetailSnackbarState,
  useRequestInitialLoading,
} from "@/view_models/atoms";
import { MonthlyBalanceSheet } from "@/view_models/balancesheetSelectors";

import NoImage from "@assets/image/NoImage.svg";
import PropertySampleImage01 from "@assets/image/property_sample_image01.png";
import PropertySampleImage02 from "@assets/image/property_sample_image02.png";
import PropertySampleImage03 from "@assets/image/property_sample_image03.png";

import { CustomChip } from "../Chip";
import { OccupancyStatus } from "../property/OccupancyStatus";

import { ContractExtendModal } from "./ContractExtendModal";

// imageが空の場合は画像3枚からランダムで表示
const sampleImages = [PropertySampleImage01, PropertySampleImage02, PropertySampleImage03];
const optimisticallyDismissedAiCreateNoticePropertyIds = new Set<number>();

const useStyles = makeStyles(() => ({
  background: { minHeight: "100vh", [theme.breakpoints.up("md")]: { paddingBottom: layout.space[40] } },
  title: {
    padding: layout.space[24],
    background: colors["surface/card"],
    [theme.breakpoints.up("md")]: { padding: "24px 0", background: "transparent" },
  },
  name: {
    ...textStyles["SP/Heading/H2"],
    marginTop: layout.space[12],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Heading/H2"] },
  },
  address: {
    ...textStyles["SP/Body/Small"],
    color: colors["text/secondary"],
    marginTop: layout.space[8],
    [theme.breakpoints.up("md")]: { ...textStyles["PC/Body/Small"] },
  },
  layout: {
    [theme.breakpoints.up("md")]: {
      display: "grid",
      gridTemplateColumns: "minmax(0,384px) minmax(0,1fr)",
      gap: layout.space[40],
      alignItems: "start",
    },
  },
  sidebar: { minWidth: 0, [theme.breakpoints.up("md")]: { position: "sticky", top: 80 } },
  media: {
    height: 240,
    backgroundSize: "cover",
    backgroundPosition: "center",
    position: "relative",
    [theme.breakpoints.up("md")]: { height: 256, borderRadius: layout.radius.lg },
  },
  photoButton: {
    position: "absolute",
    bottom: layout.space[16],
    right: layout.space[16],
    borderRadius: layout.radius.full,
    padding: "8px 16px",
    background: colors["surface/card"],
    ...textStyles["SP/Button/S"],
    color: colors["text/primary"],
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "repeat(3,minmax(0,1fr))",
    gap: layout.space[16],
    padding: layout.space[24],
    background: colors["surface/card"],
    [theme.breakpoints.up("md")]: { display: "block", padding: "12px 0", background: "transparent" },
  },
  metric: {
    minWidth: 0,
    [theme.breakpoints.up("md")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 0",
      borderBottom: `1px solid ${colors["border/default"]}`,
    },
  },
  metricLabel: { ...textStyles["SP/Body/Small"], color: colors["text/secondary"] },
  metricValue: {
    ...textStyles["Number/S"],
    marginTop: layout.space[8],
    overflowWrap: "anywhere",
    [theme.breakpoints.up("md")]: { marginTop: 0 },
  },
  unit: { ...textStyles["SP/Body/Small"] },
  main: { minWidth: 0 },
  displayControl: {
    padding: layout.space[24],
    paddingBottom: 0,
    [theme.breakpoints.up("md")]: { padding: 0, marginBottom: layout.space[24] },
  },
  navigation: {
    display: "none",
    [theme.breakpoints.up("md")]: { display: "grid", gap: layout.space[12], marginTop: layout.space[24] },
  },
  aiCreateNotice: {
    display: "flex",
    alignItems: "center",
    padding: layout.space[16],
    background: colors["status/info-bg"],
    gap: layout.space[8],
    cursor: "pointer",
  },
  aiCreateNoticeText: { ...textStyles["SP/Body/Small"], flex: 1 },
  aiCreateNoticeCloseButton: { padding: layout.space[4], color: colors["text/secondary"] },
}));

type Props = {
  children: React.ReactNode;
  propertyType: "保有中" | "検討中";
  propertyHolder: PropertyHolder;
  monthlyBalanceSheet: MonthlyBalanceSheet;
  onBack?: () => void;
  onSave?: () => void;
  onPhotoChange?: () => void;
  navigation?: React.ReactNode;
  selectedView?: string;
  sidebarContent?: React.ReactNode;
  onViewBack?: () => void;
  displayControl?: React.ReactNode;
};

export const ManagementDetailView: React.FC<Props> = (props) => {
  const { children, propertyType, propertyHolder, monthlyBalanceSheet } = props;
  const classes = useStyles();
  const { isMobile } = useDevice();
  const history = useHistory();
  const propertyId = propertyHolder.property.id;
  const fp = PropallyApiFactory();
  const requestInitialLoading = useRequestInitialLoading();
  const setManagementDetailSnackBarState = useSetRecoilState(PropertyManagementDetailSnackbarState);
  const [isAiCreateNoticeHidden, setIsAiCreateNoticeHidden] = React.useState(
    Boolean(propertyHolder.property.is_ai_create_notification_dismissed) ||
      optimisticallyDismissedAiCreateNoticePropertyIds.has(propertyId)
  );
  const [isDismissingAiCreateNotice, setIsDismissingAiCreateNotice] = React.useState(false);

  const formattedTotalPriceOfProperty = Math.round(
    propertyHolder.property.total_price_of_property / 10000
  ).toLocaleString();
  const formattedMonthlyFee = Math.round(getMonthlyRentByProperty(propertyHolder)).toLocaleString();

  // 入居中かどうか
  const isCurrentInUse = isPropertyCurrentInUse(propertyHolder.lease_contracts);

  const [isApproveModalOpen, setIsApproveModalOpen] = React.useState(false);
  const dismissedContractExtendModalPropertyIdsRef = React.useRef<Set<number>>(new Set());
  // useRecoilCallbackを使用して非同期の状態更新を行う
  const updatePropertyEditPageState = useRecoilCallback(({ snapshot, set }) => async (newState) => {
    set(PropertyEditPageState, newState);
    // 状態が更新されるのを待つ
    await snapshot.getPromise(PropertyEditPageState);
  });

  const onClickEditPropertyInfo = async () => {
    history.push("/management/detail/settings");
  };

  const handleDismissAiCreateNotice = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    optimisticallyDismissedAiCreateNoticePropertyIds.add(propertyId);
    setIsAiCreateNoticeHidden(true);
    setIsDismissingAiCreateNotice(true);

    try {
      const result = await fp.v1PropertiesPropertyIdAiCreateNotificationDismissedPatch(
        propertyId,
        { is_ai_create_notification_dismissed: true },
        { withCredentials: true }
      );

      if (result.status === 200) {
        requestInitialLoading();
      }
    } catch (e) {
      optimisticallyDismissedAiCreateNoticePropertyIds.delete(propertyId);
      setIsAiCreateNoticeHidden(false);
      setManagementDetailSnackBarState({
        message: "AI利用通知を閉じられませんでした",
        status: "error",
        isOpen: true,
      });
    } finally {
      setIsDismissingAiCreateNotice(false);
    }
  };

  React.useEffect(() => {
    // 区分マンションが空室中の場合は、シミュレーション結果を表示している旨と
    // 賃貸契約情報を更新してほしい旨のモーダルを表示
    const shouldShowContractExtendModal =
      !isCurrentInUse &&
      propertyHolder.property.property_category !== "一棟物件" &&
      !dismissedContractExtendModalPropertyIdsRef.current.has(propertyId);

    if (shouldShowContractExtendModal) {
      setIsApproveModalOpen(true);
      return;
    }

    // 物件切り替え時に前物件の表示状態を引きずらないよう、条件を満たさない場合は閉じる
    setIsApproveModalOpen(false);
  }, [isCurrentInUse, propertyHolder.property.property_category, propertyId]);

  React.useEffect(() => {
    if (propertyHolder.property.is_ai_create_notification_dismissed) {
      optimisticallyDismissedAiCreateNoticePropertyIds.delete(propertyId);
    }

    setIsAiCreateNoticeHidden(
      Boolean(propertyHolder.property.is_ai_create_notification_dismissed) ||
        optimisticallyDismissedAiCreateNoticePropertyIds.has(propertyId)
    );
  }, [propertyId, propertyHolder.property.is_ai_create_notification_dismissed]);

  const shouldShowAiCreateNotice =
    propertyHolder.property.is_need_ai_create_notification &&
    !propertyHolder.property.is_ai_create_notification_dismissed &&
    !isAiCreateNoticeHidden;

  const subTitle =
    props.selectedView === "property"
      ? "物件情報"
      : props.selectedView === "simulation"
      ? "シミュレーション"
      : "周辺の成約事例";
  const pageHeading = (
    <Box display={{ xs: "none", md: "block" }} py={3}>
      <ButtonBase onClick={props.onViewBack} style={{ color: colors["action/primary"], minHeight: 44 }}>
        物件詳細
      </ButtonBase>
      <Typography component="h1" style={textStyles["PC/Heading/H2"]}>
        {subTitle}
      </Typography>
    </Box>
  );
  if (props.selectedView === "property" || props.selectedView === "nearby")
    return (
      <Box style={{ maxWidth: 776, margin: "0 auto", width: "100%" }}>
        {pageHeading}
        {children}
      </Box>
    );
  const coverImage =
    propertyHolder.cover_image_url && propertyHolder.cover_image_url !== NoImage
      ? propertyHolder.cover_image_url
      : sampleImages[propertyId % sampleImages.length];
  return (
    <Box className={classes.background}>
      <Box display={{ xs: "none", md: "block" }}>
        {props.selectedView === "simulation" ? (
          pageHeading
        ) : (
          <Box className={classes.title}>
            {props.onBack && (
              <ButtonBase onClick={props.onBack} style={{ color: colors["action/primary"], minHeight: 44 }}>
                {propertyType === "保有中" ? "保有物件" : "検討中物件"}
              </ButtonBase>
            )}
            <Box display="flex" alignItems="center" gridGap={8}>
              <CustomChip label={propertyType} outlined color={propertyType === "検討中" ? "default" : undefined} />
              {propertyHolder.property.property_category !== "一棟物件" && (
                <OccupancyStatus label={isCurrentInUse ? "入居中" : "空室中"} isActive={isCurrentInUse} />
              )}
            </Box>
            <Typography component="h1" className={classes.name}>
              {propertyHolder.property.name}
            </Typography>
            <Typography className={classes.address}>{getPropertyAddressString(propertyHolder)}</Typography>
          </Box>
        )}
      </Box>
      <Box className={classes.layout}>
        {(!isMobile || !props.selectedView || props.selectedView === "operation") && (
          <Box className={classes.sidebar}>
            {props.sidebarContent ?? (
              <>
                <Box className={classes.media} style={{ backgroundImage: `url(${coverImage})` }}>
                  {props.onPhotoChange && (
                    <ButtonBase className={classes.photoButton} onClick={props.onPhotoChange}>
                      写真を変更
                    </ButtonBase>
                  )}
                </Box>
                <Box display={{ xs: "block", md: "none" }}>
                  <Box className={classes.title}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <CustomChip
                        label={propertyType}
                        outlined
                        color={propertyType === "検討中" ? "default" : undefined}
                      />
                      {propertyHolder.property.property_category !== "一棟物件" && (
                        <OccupancyStatus label={isCurrentInUse ? "入居中" : "空室中"} isActive={isCurrentInUse} />
                      )}
                    </Box>
                    <Typography component="h1" className={classes.name}>
                      {propertyHolder.property.name}
                    </Typography>
                    <Typography className={classes.address}>{getPropertyAddressString(propertyHolder)}</Typography>
                  </Box>
                </Box>
                <Box className={classes.metrics}>
                  <Box className={classes.metric}>
                    <Typography className={classes.metricLabel}>購入価格</Typography>
                    <Typography className={classes.metricValue}>
                      {formattedTotalPriceOfProperty}
                      <span className={classes.unit}>万円</span>
                    </Typography>
                  </Box>
                  <Box className={classes.metric}>
                    <Typography className={classes.metricLabel}>
                      {propertyHolder.property.property_category === "一棟物件" ? "管理中部屋数" : "家賃（管理費込）"}
                    </Typography>
                    <Typography className={classes.metricValue}>
                      {propertyHolder.property.property_category === "一棟物件"
                        ? propertyHolder.building_room.length
                        : formattedMonthlyFee}
                      <span className={classes.unit}>
                        {propertyHolder.property.property_category === "一棟物件" ? "部屋" : "円"}
                      </span>
                    </Typography>
                  </Box>
                  <Box className={classes.metric}>
                    <Typography className={classes.metricLabel}>表面利回り / 実質利回り</Typography>
                    <Typography className={classes.metricValue}>
                      {formatYieldRateForDisplay(getYieldRateByProperty(propertyHolder))} /{" "}
                      {formatYieldRateForDisplay(
                        getActualYieldRateByProperty(propertyHolder, {
                          annualPropertyTax: monthlyBalanceSheet.result.property_tax_of_last_year,
                        })
                      )}
                    </Typography>
                  </Box>
                </Box>
                {!isMobile && <Box className={classes.navigation}>{props.navigation}</Box>}
              </>
            )}
          </Box>
        )}
        <Box className={classes.main}>
          {props.displayControl && <Box className={classes.displayControl}>{props.displayControl}</Box>}
          {shouldShowAiCreateNotice && (
            <Box
              className={classes.aiCreateNotice}
              role="button"
              tabIndex={0}
              onClick={onClickEditPropertyInfo}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onClickEditPropertyInfo();
              }}
            >
              <Typography className={classes.aiCreateNoticeText}>
                AI自動登録物件です：実際の数値情報がわかる方はこちらで修正を
              </Typography>
              <IconButton
                aria-label="AI自動登録物件通知を閉じる"
                className={classes.aiCreateNoticeCloseButton}
                onClick={handleDismissAiCreateNotice}
                disabled={isDismissingAiCreateNotice}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {children}
        </Box>
      </Box>
      {/* ここにモーダルを入れれば良い */}
      {isApproveModalOpen && (
        <ContractExtendModal
          onClose={() => {
            dismissedContractExtendModalPropertyIdsRef.current.add(propertyId);
            setIsApproveModalOpen(false);
          }}
          onClickUpdate={async () => {
            await updatePropertyEditPageState("lease_contract");
            history.push("/edit_property");
          }}
        />
      )}
    </Box>
  );
};
